import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Search, Calendar, Users, X, ChevronDown, Plus, Minus } from "lucide-react";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import MoheliImg   from "../clients/data/moheli.jpeg";


const G      = "#3A9D23";
const G_DARK = "#2f7d1c";
const API    = "https://api.komorotel.com";

const formatDisplay = (date) =>
  date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });

const formatAPI = (date) =>
  new Date(date).toLocaleDateString("sv-SE");

export default function SearchBar() {
  const navigate = useNavigate();

  const [destination, setDestination] = useState("");
  const [hotels,      setHotels]      = useState([]);
  const [islands,     setIslands]     = useState([]);
  const [dropOpen,    setDropOpen]    = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [destError,   setDestError]   = useState(false);

  const today    = new Date();
  const tomorrow = new Date(); tomorrow.setDate(today.getDate() + 1);
  const [dateRange,   setDateRange]   = useState([{ startDate: today, endDate: tomorrow, key: "selection" }]);
  const [showCal,     setShowCal]     = useState(false);
  const [adults,      setAdults]      = useState(2);
  const [children,    setChildren]    = useState(0);
  const [showGuests,  setShowGuests]  = useState(false);

  const wrapRef  = useRef();
  const calRef   = useRef();
  const guestRef = useRef();

  /* Fermer au clic extérieur */
  useEffect(() => {
    const h = (e) => {
      if (wrapRef.current  && !wrapRef.current.contains(e.target))  { setDropOpen(false); }
      if (calRef.current   && !calRef.current.contains(e.target))   setShowCal(false);
      if (guestRef.current && !guestRef.current.contains(e.target)) setShowGuests(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  /* Autocomplete backend */
  useEffect(() => {
    if (isSelecting) { setIsSelecting(false); return; }
    if (!destination.trim()) { setHotels([]); setIslands([]); setDropOpen(false); return; }

    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/api/hotels/search-suggestions`, {
          params: { destination }
        });
        // data = array mixte : hotels (ont .name), cities (ont .city), islands (ont .island)
        setHotels(data.filter(r => r.name).slice(0, 4));
        // Pour destinations populaires : on prend villes + îles distinctes
        const dest = [
          ...data.filter(r => !r.name && r.city).map(r => ({ label: r.city, type: "city", image: null })),
          ...data.filter(r => !r.name && !r.city && r.island).map(r => ({ label: r.island, type: "island", image: null })),
        ].slice(0, 3);
        setIslands(dest);
        setDropOpen(true);
      } catch { /* silencieux */ }
    }, 300);
    return () => clearTimeout(t);
  }, [destination]);

  useEffect(() => { if (destination) setDestError(false); }, [destination]);

  const isInvalid = dateRange[0].endDate <= dateRange[0].startDate;

  const handleSearch = () => {
    if (!destination.trim()) { setDestError(true); return; }
    if (isInvalid) { setShowCal(true); return; }
    navigate(`/hotels?destination=${encodeURIComponent(destination)}&checkIn=${formatAPI(dateRange[0].startDate)}&checkOut=${formatAPI(dateRange[0].endDate)}&guests=${adults + children}`);
  };

  const selectHotel = (h) => {
    setIsSelecting(true);
    setDestination(h.name);
    setDropOpen(false);
  };

  const selectDest = (label) => {
    setIsSelecting(true);
    setDestination(label);
    setDropOpen(false);
  };

  const guestsLabel = `${adults} adulte${adults > 1 ? "s" : ""}${children > 0 ? ` · ${children} enfant${children > 1 ? "s" : ""}` : ""}`;
  const showDrop = dropOpen && (hotels.length > 0 || islands.length > 0);

  return (
    <div ref={wrapRef} style={{ width: "100%", maxWidth: 900, position: "relative", boxSizing: "border-box" }}>

      {/* ── BARRE ── */}
      <div className="searchbar-inner" style={{ background: "#fff", borderRadius: 16, boxShadow: "0 6px 40px rgba(0,0,0,0.14)", padding: "10px", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0 }}>

        {/* Destination */}
        <div style={{ position: "relative", flex: 2, paddingRight: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Search size={16} color="#9ca3af" style={{ flexShrink: 0 }} />
            <input
              value={destination}
              onChange={e => { setDestination(e.target.value); }}
              onFocus={() => { if (destination.trim()) setDropOpen(true); }}
              placeholder="Destination, hôtel..."
              style={{ border: "none", outline: "none", fontSize: 14, color: destError ? "#ef4444" : "#111827", background: "transparent", width: "100%", fontFamily: "inherit" }}
            />
            {destination && (
              <button onClick={() => { setDestination(""); setHotels([]); setIslands([]); setDropOpen(false); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: 2, flexShrink: 0 }}>
                <X size={14} />
              </button>
            )}
          </div>
          {/* Tooltip erreur */}
          {destError && (
            <div style={{ position: "absolute", bottom: "calc(100% + 10px)", left: 0, background: "#ef4444", color: "#fff", fontSize: 11, fontWeight: 600, padding: "5px 10px", borderRadius: 7, whiteSpace: "nowrap", zIndex: 300 }}>
              Veuillez entrer une destination
              <div style={{ position: "absolute", bottom: -4, left: 14, width: 8, height: 8, background: "#ef4444", transform: "rotate(45deg)" }} />
            </div>
          )}
        </div>

        <div style={{ width: 1, height: 38, background: "#e5e7eb", flexShrink: 0, margin: "0 4px" }} />

        {/* Dates */}
        <div ref={calRef} style={{ position: "relative", flex: 1.4 }}>
          <div onClick={() => { setShowCal(!showCal); setShowGuests(false); setDropOpen(false); }}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 14px", cursor: "pointer" }}>
            <Calendar size={14} color={G} style={{ flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 2 }}>Dates</div>
              <div style={{ fontSize: 13, color: "#111827", fontWeight: 500, whiteSpace: "nowrap" }}>
                {formatDisplay(dateRange[0].startDate)} → {formatDisplay(dateRange[0].endDate)}
              </div>
            </div>
            <ChevronDown size={13} color="#9ca3af" style={{ marginLeft: "auto", transition: "transform 0.2s", transform: showCal ? "rotate(180deg)" : "none" }} />
          </div>
          {showCal && (
            <div style={{ position: "absolute", top: "calc(100% + 14px)", left: "50%", transform: "translateX(-50%)", background: "#fff", borderRadius: 16, boxShadow: "0 8px 40px rgba(0,0,0,0.14)", border: "1px solid #f3f4f6", zIndex: 250, overflow: "hidden" }}>
              <DateRange editableDateInputs onChange={item => setDateRange([item.selection])} moveRangeOnFirstSelection={false} ranges={dateRange} minDate={new Date()} rangeColors={[G]} />
              <div style={{ padding: "0 16px 14px", textAlign: "right" }}>
                <button onClick={() => setShowCal(false)}
                  style={{ background: G, color: "#fff", border: "none", borderRadius: 8, padding: "8px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  Confirmer
                </button>
              </div>
            </div>
          )}
        </div>

        <div style={{ width: 1, height: 38, background: "#e5e7eb", flexShrink: 0, margin: "0 4px" }} />

        {/* Voyageurs */}
        <div ref={guestRef} style={{ position: "relative", flex: 1 }}>
          <div onClick={() => { setShowGuests(!showGuests); setShowCal(false); setDropOpen(false); }}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 14px", cursor: "pointer" }}>
            <Users size={14} color={G} style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 2 }}>Voyageurs</div>
              <div style={{ fontSize: 13, color: "#111827", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{guestsLabel}</div>
            </div>
            <ChevronDown size={13} color="#9ca3af" style={{ transition: "transform 0.2s", transform: showGuests ? "rotate(180deg)" : "none" }} />
          </div>
          {showGuests && (
            <div style={{ position: "absolute", top: "calc(100% + 14px)", left: "50%", transform: "translateX(-50%)", width: 280, background: "#fff", borderRadius: 16, boxShadow: "0 8px 40px rgba(0,0,0,0.14)", border: "1px solid #f3f4f6", padding: "6px 20px 16px", zIndex: 250 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 14, marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>Voyageurs</span>
                <button onClick={() => setShowGuests(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af" }}><X size={15} /></button>
              </div>
              <div style={{ height: 1, background: "#f3f4f6", margin: "0 -20px 4px" }} />
              <GuestRow label="Adultes" sub="13 ans et plus" value={adults} onDec={() => setAdults(Math.max(1, adults-1))} onInc={() => setAdults(adults+1)} min={1} />
              <div style={{ height: 1, background: "#f3f4f6" }} />
              <GuestRow label="Enfants" sub="2 à 12 ans" value={children} onDec={() => setChildren(Math.max(0, children-1))} onInc={() => setChildren(children+1)} min={0} />
              <div style={{ height: 1, background: "#f3f4f6", marginBottom: 14 }} />
              <button onClick={() => setShowGuests(false)}
                style={{ width: "100%", background: G, color: "#fff", border: "none", borderRadius: 10, padding: "10px 0", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = G_DARK}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = G}>
                Appliquer
              </button>
            </div>
          )}
        </div>

        {/* Bouton rechercher */}
        <button onClick={handleSearch}
          style={{ display: "flex", alignItems: "center", gap: 7, background: G, color: "#fff", border: "none", borderRadius: 11, padding: "14px 22px", cursor: "pointer", transition: "background 0.18s", whiteSpace: "nowrap", flexShrink: 0, fontSize: 14, fontWeight: 700 }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = G_DARK}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = G}>
          <Search size={15} color="#fff" />
          Rechercher
        </button>
      </div>

      {/* ── DROPDOWN STYLE ANCIENNE VERSION ── */}
      {showDrop && (
        <div style={{
          position: "absolute", top: "calc(100% + 8px)", left: 0, right: 0,
          background: "#fff", borderRadius: 16,
          boxShadow: "0 8px 48px rgba(0,0,0,0.15)",
          display: "grid", gridTemplateColumns: "1fr 1px 1fr",
          overflow: "hidden", zIndex: 9999,
        }}>

          {/* Col gauche — Hébergements suggérés */}
          <div style={{ padding: "18px 20px" }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9ca3af", marginBottom: 14 }}>
              Hébergements suggérés
            </div>

            {hotels.map((h, i) => (
              <div key={i}
                onClick={() => selectHotel(h)}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 8px", borderRadius: 10, cursor: "pointer", marginBottom: 2, transition: "background 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                {/* Photo */}
                <img
                  src={h.photos?.[0] || `https://images.unsplash.com/photo-1566073771259-6a8506099945?w=80&q=70`}
                  alt={h.name}
                  style={{ width: 48, height: 40, borderRadius: 8, objectFit: "cover", flexShrink: 0, background: "#f3f4f6" }}
                />
                {/* Infos */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{h.name}</div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}>{h.city}{h.island ? `, ${h.island}` : ""}</div>
                </div>
                {/* Prix */}
                {h.minPrice && (
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{h.minPrice}€</div>
                    <div style={{ fontSize: 10, color: "#9ca3af" }}>/ nuit</div>
                  </div>
                )}
              </div>
            ))}

            <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 10, marginTop: 6 }}>
              <button
                onClick={() => { navigate(`/hotels?destination=${encodeURIComponent(destination)}`); setDropOpen(false); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: G, fontSize: 12, fontWeight: 600, padding: 0 }}>
                Voir tous les résultats pour "{destination}" →
              </button>
            </div>
          </div>

          {/* Séparateur */}
          <div style={{ background: "#f3f4f6" }} />

          {/* Col droite — Destinations populaires */}
          <div style={{ padding: "18px 20px" }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9ca3af", marginBottom: 14 }}>
              Destinations populaires
            </div>

            {/* Si pas de résultats depuis l'API, on affiche les 3 îles par défaut */}
            {(islands.length > 0 ? islands : [
              { label: "Ngazidja", type: "island", image: "https://www.vanilla-islands.org/wp-content/uploads/2015/12/accueil-comores-plage-barque.jpg" },
              { label: "Anjouan",       type: "island", image: "https://www.nomade-aventure.com/Content/Images/ImgProduits/COM/11221/701448_QuatreTiers.ori.jpg"  },
              { label: "Moheli",        type: "island", image: MoheliImg  },
            ]).map((d, i) => (
              <div key={i}
                onClick={() => selectDest(d.label)}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 8px", borderRadius: 10, cursor: "pointer", marginBottom: 2, transition: "background 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <img
                  src={d.image || "https://www.vanilla-islands.org/wp-content/uploads/2015/12/accueil-comores-plage-barque.jpg"}
                  alt={d.label}
                  style={{ width: 48, height: 40, borderRadius: 8, objectFit: "cover", flexShrink: 0, background: "#f3f4f6" }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{d.label}</div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1, textTransform: "capitalize" }}>{d.type === "island" ? "Île" : "Ville"}</div>
                </div>
                <div style={{ color: "#d1d5db", fontSize: 14, flexShrink: 0 }}>→</div>
              </div>
            ))}

            <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 10, marginTop: 6 }}>
              <button
                onClick={() => { navigate("/destinations"); setDropOpen(false); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: G, fontSize: 12, fontWeight: 600, padding: 0 }}>
                Voir toutes les destinations →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── CSS SearchBar mobile ── */
if (typeof document !== "undefined") {
  const styleId = "searchbar-mobile-css";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @media (max-width: 768px) {
        .searchbar-inner {
          flex-direction: column !important;
          gap: 0 !important;
          padding: 12px !important;
        }
        .searchbar-inner > div {
          width: 100% !important;
          flex: none !important;
          border-right: none !important;
          border-bottom: 1px solid #e5e7eb !important;
          padding: 10px 12px !important;
        }
        .searchbar-inner > div:last-of-type {
          border-bottom: none !important;
        }
        .searchbar-inner > button {
          width: 100% !important;
          margin-top: 8px !important;
          border-radius: 10px !important;
          padding: 13px 0 !important;
        }
        .searchbar-inner > [style*="width: 1px"] {
          display: none !important;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

/* ── Compteur voyageurs ── */
function GuestRow({ label, sub, value, onDec, onInc, min }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 0" }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{label}</div>
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}>{sub}</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onDec} disabled={value <= min}
          style={{ width: 30, height: 30, borderRadius: "50%", border: `1.5px solid ${value <= min ? "#e5e7eb" : G}`, background: "none", cursor: value <= min ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: value <= min ? "#d1d5db" : G }}>
          <Minus size={13} />
        </button>
        <span style={{ fontSize: 15, fontWeight: 700, color: "#111827", minWidth: 18, textAlign: "center" }}>{value}</span>
        <button onClick={onInc}
          style={{ width: 30, height: 30, borderRadius: "50%", border: `1.5px solid ${G}`, background: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: G }}
          onMouseEnter={e => { e.currentTarget.style.background = G; e.currentTarget.style.color = "#fff"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = G; }}>
          <Plus size={13} />
        </button>
      </div>
    </div>
  );
}